import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Search, ArrowLeft, GitBranch, FileText } from 'lucide-react';

import { docsService } from '../../services/docs/docsService';
import type { SearchResult } from '../../services/docs/types';

const PROCEDURE_TYPE_LABELS: Record<string, string> = {
  procedure: 'Procedure',
  engineering_document: 'Eng Doc',
  form: 'Form',
  policy: 'Policy',
};

const DocsSearchPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const query = searchParams.get('q') || '';
  const [results, setResults] = useState<SearchResult[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState(query);

  useEffect(() => {
    if (!query.trim()) return;

    const doSearch = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await docsService.search(query);
        setResults(data.results);
        setTotal(data.total);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Search failed');
      } finally {
        setLoading(false);
      }
    };

    doSearch();
  }, [query]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchInput.trim()) {
      setSearchParams({ q: searchInput.trim() });
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <button
        onClick={() => navigate('/docs')}
        className="flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4"
      >
        <ArrowLeft className="w-4 h-4 mr-1" />
        Back to Docs
      </button>

      {/* Search bar */}
      <form onSubmit={handleSearch} className="mb-6">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search procedures, processes, forms..."
            className="block w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
            autoFocus
          />
        </div>
      </form>

      {/* Results count */}
      {query && !loading && (
        <p className="text-sm text-gray-500 mb-4">
          {total} result{total !== 1 ? 's' : ''} for "{query}"
        </p>
      )}

      {/* Loading */}
      {loading && (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="animate-pulse h-20 bg-gray-200 rounded-lg"
            />
          ))}
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm">
          {error}
        </div>
      )}

      {/* Results */}
      {!loading && results.length > 0 && (
        <div className="space-y-2">
          {results.map((result) => (
            <Link
              key={`${result.resultType}-${result.id}`}
              to={
                result.resultType === 'process'
                  ? `/docs/processes/${result.id}`
                  : `/docs/procedures/${result.id}`
              }
              className="block p-4 bg-white rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-colors"
            >
              <div className="flex items-start">
                {result.resultType === 'process' ? (
                  <GitBranch className="w-4 h-4 text-blue-500 mt-0.5 mr-3 flex-shrink-0" />
                ) : (
                  <FileText className="w-4 h-4 text-purple-500 mt-0.5 mr-3 flex-shrink-0" />
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900">
                      {result.title}
                    </span>
                    <span className="text-xs px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded">
                      {result.resultType === 'process'
                        ? 'Process'
                        : PROCEDURE_TYPE_LABELS[result.procedureType || ''] ||
                          result.resultType}
                    </span>
                  </div>
                  {result.summary && (
                    <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                      {result.summary}
                    </p>
                  )}
                  {result.tags.length > 0 && (
                    <div className="flex gap-1 mt-2">
                      {result.tags.map((tag) => (
                        <span
                          key={tag}
                          className="px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded text-xs"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}

      {/* No results */}
      {!loading && query && results.length === 0 && !error && (
        <div className="bg-white rounded-lg border border-gray-200 p-8 text-center text-gray-400">
          No results found for "{query}". Try different keywords.
        </div>
      )}
    </div>
  );
};

export default DocsSearchPage;
